// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { GraduationCap, Loader2, Eye, EyeOff, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [nameErrors, setNameErrors] = useState({ first_name: "", last_name: "" });
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
  });

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const sanitizeName = (value) => {
    // Strip HTML tags
    return value.replace(/<[^>]*>/g, '').replace(/[<>{}[\]\\;]/g, '');
  };

  const validateName = (value, fieldLabel) => {
    if (!value.trim()) return "";
    const nameRegex = /^[a-zA-Z\u00C0-\u00FF\s\-'.]+$/;
    if (!nameRegex.test(value)) {
      return `Le ${fieldLabel} ne doit contenir que des lettres, espaces, tirets ou apostrophes`;
    }
    if (value.trim().length < 2) {
      return `Le ${fieldLabel} doit contenir au moins 2 caractères`;
    }
    if (value.trim().length > 50) {
      return `Le ${fieldLabel} ne doit pas dépasser 50 caractères`;
    }
    return "";
  };

  const handleNameChange = (field, value) => {
    const sanitized = sanitizeName(value);
    setFormData({ ...formData, [field]: sanitized });
    const label = field === 'first_name' ? 'prénom' : 'nom';
    setNameErrors(prev => ({ ...prev, [field]: validateName(sanitized, label) }));
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData({ ...formData, email });
    if (email && !isValidEmail(email)) {
      setEmailError("Veuillez utiliser une adresse e-mail valide provenant d’un domaine reconnu (ex : Gmail, Outlook, Yahoo…).");
    } else {
      setEmailError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(formData.email)) { setEmailError("Merci de saisir un email valide"); return; }
    if (formData.password !== formData.confirmPassword) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (formData.password.length < 6) { toast.error("Le mot de passe doit contenir au moins 6 caractères"); return; }

    // Validate names
    const firstNameErr = validateName(formData.first_name, 'prénom');
    const lastNameErr = validateName(formData.last_name, 'nom');
    if (firstNameErr || lastNameErr) {
      setNameErrors({ first_name: firstNameErr, last_name: lastNameErr });
      if (firstNameErr) toast.error(firstNameErr);
      else toast.error(lastNameErr);
      return;
    }
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error("Le prénom et le nom sont requis");
      return;
    }

    setLoading(true);
    try {
      const response = await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: "candidate",
      });
      if (response && response.is_approved === false) {
        toast.success("Compte créé ! En attente d'approbation.", { duration: 5000 });
        navigate("/login?pending=true");
      } else {
        toast.success("Compte créé avec succès !");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "h-12 rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-400 focus:ring-violet-400/20 transition-all text-base placeholder:text-gray-400";

  return (
    <div className="flex min-h-screen overflow-hidden">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Animated gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-violet-700 to-purple-800 animate-gradient" />

        {/* Mesh */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />

        {/* Floating orbs */}
        <div className="absolute top-32 right-32 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-32 left-16 w-64 h-64 bg-purple-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }} />
        <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-pink-400/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between w-full p-12 lg:p-16">
          {/* Top */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20">
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white tracking-tight">DevOpsLearn</span>
            </div>
            <p className="text-sm text-white/60 tracking-widest uppercase font-medium">Plateforme de Formation</p>
          </div>

          {/* Center */}
          <div className="space-y-8">
            <h1 className="text-5xl xl:text-6xl font-extrabold text-white leading-tight tracking-tight">
              Rejoignez la<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-blue-200 to-indigo-200">
                communauté
              </span><br />
              DevOpsLearn
            </h1>
            <p className="text-lg text-white/70 max-w-md leading-relaxed">
              Créez votre compte et commencez à apprendre dès aujourd'hui. Accédez à des formations Cloud & DevOps de qualité.
            </p>

            {/* Stats */}
            <div className="flex gap-8">
              {[
                { value: "100%", label: "En ligne" },
                { value: "24/7", label: "Disponible" },
                { value: "∞", label: "Ressources" },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-3xl font-extrabold text-white">{s.value}</div>
                  <div className="text-xs text-white/50 mt-1 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] font-bold text-white/30 tracking-[0.2em] uppercase">
              KHLIFI HOUCEM — Cloud & DevSecOps
            </span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
        </div>
      </div>

      {/* Right Panel - Register Form */}
      <div className="w-full lg:w-[45%] flex flex-col items-center justify-center px-6 sm:px-12 lg:px-16 bg-white relative overflow-y-auto">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle, #7c3aed 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }} />

        <div className="relative z-10 w-full max-w-sm py-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/30">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">DevOpsLearn</span>
          </div>

          {/* Header */}
          <div className="mb-8 flex flex-col items-center text-center">
            {/* Boîte de l'entreprise (Logo) optimisé */}
            <div className="bg-[#0B1120] p-4 rounded-3xl shadow-xl shadow-slate-200/60 mb-8 w-32 h-32 flex items-center justify-center ring-1 ring-slate-900/5 transition-transform hover:scale-105">
              <img src="/logo.png" alt="Logo Entreprise" className="w-full h-full object-contain" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
              Créer un compte ✨
            </h2>
            <p className="mt-2 text-gray-500">
              Inscrivez-vous en tant que candidat pour accéder aux formations
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="first_name" className="text-gray-700 font-medium text-sm">Prénom</Label>
                <Input
                  id="first_name" placeholder="Jean"
                  value={formData.first_name}
                  onChange={(e) => handleNameChange('first_name', e.target.value)}
                  required data-testid="register-firstname-input"
                  maxLength={50}
                  className={`${inputClass} ${nameErrors.first_name ? "border-red-400 focus:border-red-400 focus:ring-red-400/20" : ""}`}
                />
                {nameErrors.first_name && <p className="text-xs text-red-500 mt-1">{nameErrors.first_name}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="last_name" className="text-gray-700 font-medium text-sm">Nom</Label>
                <Input
                  id="last_name" placeholder="Dupont"
                  value={formData.last_name}
                  onChange={(e) => handleNameChange('last_name', e.target.value)}
                  required data-testid="register-lastname-input"
                  maxLength={50}
                  className={`${inputClass} ${nameErrors.last_name ? "border-red-400 focus:border-red-400 focus:ring-red-400/20" : ""}`}
                />
                {nameErrors.last_name && <p className="text-xs text-red-500 mt-1">{nameErrors.last_name}</p>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-gray-700 font-medium text-sm">Email</Label>
              <Input
                id="email" type="email" placeholder="votre@email.com"
                value={formData.email}
                onChange={handleEmailChange}
                required data-testid="register-email-input"
                className={`${inputClass} ${emailError ? "border-red-400 focus:border-red-400 focus:ring-red-400/20" : ""}`}
              />
              {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
            </div>



            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-gray-700 font-medium text-sm">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password" type={showPassword ? "text" : "password"} placeholder="Min. 6 caractères"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required data-testid="register-password-input"
                  className={`${inputClass} pr-12`}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-gray-700 font-medium text-sm">Confirmer</Label>
              <Input
                id="confirmPassword" type="password" placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required data-testid="register-confirm-password-input"
                className={inputClass}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-xl text-base font-semibold bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 hover:-translate-y-0.5 mt-2"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Créer mon compte
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Déjà un compte ?{" "}
              <Link to="/login" className="font-semibold text-violet-600 hover:text-violet-700 transition-colors" data-testid="register-login-link">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
